/**
 * context/app/useYearData.js - the year-filtered lists and the pipeline stats.
 * Moved verbatim out of AppProvider (AppContext.jsx), which calls it at the
 * same point (same hook order) with the state it reads.
 */
import { useMemo } from 'react';
import { quoteYear } from './rows';

export function useYearData({ selectedYear, allProjects, allOpportunities }) {
  /* ── Year-filtered data ── */
  /* selectedYear can be:
     - 'all'           → no filter
     - a single number (2026, 2025, ...)
     - an array [2025, 2026] → multi-year (matches if year is in the set)
   */
  const isValidRecord = (p) => !!(p.customer && p.customer.trim()) && !!(p.quoteDate || p.quote_date);

  const matchesYearFilter = (rowYear) => {
    if (selectedYear === 'all') return true;
    if (Array.isArray(selectedYear)) return selectedYear.includes(rowYear);
    return rowYear === selectedYear;
  };

  // quoteYear, not new Date().getFullYear(): a 1 January quote stays in its own year
  const opportunitiesThisYear = useMemo(
    () =>
      allProjects.filter((p) => {
        if (!isValidRecord(p)) return false;
        if (selectedYear === 'all') return true;
        const date = p.quoteDate || p.quote_date || p.createdAt || p.created_at;
        if (!date) return false;
        return matchesYearFilter(quoteYear(date));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allProjects, selectedYear]
  );

  const projectsThisYear = useMemo(
    () =>
      allProjects.filter((p) => {
        if (!isValidRecord(p)) return false;
        if (selectedYear === 'all') return true;
        const date = p.quoteDate || p.quote_date || p.createdAt || p.created_at;
        if (!date) return false;
        return matchesYearFilter(quoteYear(date));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allProjects, selectedYear]
  );

  /* ── Computed / Derived Data ── */
  const pipelineStats = useMemo(() => {
    const open = allOpportunities.filter((o) => (o.status || '').toLowerCase() === 'open');

    /* Win rate from actual opportunity records (Dynamics export has Won/Lost with dates) */
    const won = allOpportunities.filter((o) => (o.status || '').toLowerCase() === 'won');
    const lost = allOpportunities.filter((o) => (o.status || '').toLowerCase() === 'lost');
    const totalClosed = won.length + lost.length;
    const winRate = totalClosed > 0 ? parseFloat(((won.length / totalClosed) * 100).toFixed(1)) : 0;

    /* Win rate from bid tracking projects (more accurate — 532 real project records) */
    const wonProjects = allProjects.filter((p) => (p.status || '').toLowerCase() === 'won');
    const lostProjects = allProjects.filter((p) => (p.status || '').toLowerCase() === 'lost');
    const closedProjects = wonProjects.length + lostProjects.length;
    const winRateBidTracking = closedProjects > 0 ? parseFloat(((wonProjects.length / closedProjects) * 100).toFixed(1)) : 0;

    /* Pipeline value from open bids (status Open; former Budget bids are Open now) */
    const openProjects = allProjects.filter((p) => (p.status || '').toLowerCase() === 'open');
    const totalPipelineValue = openProjects.reduce((sum, p) => {
      const val = parseFloat(p.quotedPrice || p.quoted_price || 0);
      return sum + (isFinite(val) && val < 500000000 ? val : 0);
    }, 0);

    return {
      totalOpen: open.length,
      totalWon: won.length,
      totalLost: lost.length,
      winRate,
      wonProjects: wonProjects.length,
      lostProjects: lostProjects.length,
      winRateBidTracking,
      openBidCount: openProjects.length,
      totalPipelineValue,
    };
  }, [allOpportunities, allProjects]);

  return { opportunitiesThisYear, projectsThisYear, pipelineStats };
}
